import { type ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import { SvgIcon } from "@mui/material"
import { Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { KonanAvatar } from "../../../../../../components/core/Avatar"
import { setCurrentModel } from "../../../../../../features/Models"
import type { RootState } from "../../../../../../store/ReduxStore"
import { MODEL_STATE_TAG_MAPPER } from "../../../../../../utils/constants"
import type { ModelListItemProps } from "./Interfaces"

import styles from "./ModelListItem.module.scss"

// Function to get localized model state names
const getLocalizedModelStateName = (state: keyof typeof MODEL_STATE_TAG_MAPPER, t: (key: string) => string): string => {
  const stateMap = {
    live: t("states.live"),
    challenger: t("states.challenger"),
    disabled: t("states.idle"),
    "pending-action": t("states.pendingAction"),
  }
  return stateMap[state]
}

/**
 * A list item for a model in the ModelsList component.
 *
 * @param { Model } model the model item to rendered
 * @returns {ReactElement} A list item with a link to the model view page.
 */
export function ModelListItem({ model, lastItemRef }: ModelListItemProps): ReactElement {
  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const { t } = useTranslation("models")

  const pathname = useLocation().pathname

  const isSelected = currentModel?.uuid === model.uuid

  const dispatch = useDispatch()

  const userEmail = useSelector((state: RootState) => state.auth.email)

  return (
    <li onClick={() => dispatch(setCurrentModel(model))} ref={lastItemRef}>
      <Link to={`${pathname}?model_id=${model.uuid}`} style={{ textDecoration: "none" }}>
        <div aria-selected={isSelected ? "true" : "false"} className={styles.SidebarButton}>
          <Tooltip title={model.created_by} placement="right">
            <SvgIcon className={styles.avatar}>
              <KonanAvatar size={24} name={model.created_at ? model?.created_by : userEmail ? userEmail : "User"} />
            </SvgIcon>
          </Tooltip>

          <div className={styles.sideBarButtonContentWrapper}>
            <div className={styles.sidebarButtonTitle}>
              <div className={styles.truncateText}>
                <Typography variant="label-bold" noWrap variantColor={isSelected ? 1 : 2} title={model?.name}>
                  {model?.name}
                </Typography>
              </div>
            </div>

            <div className={styles.sideBarButtonDate}>
              <Tooltip title={moment(model?.created_at).format("DD/MM/YYYY, h:mm A")} placement="right">
                <Typography variant="label-regular" noWrap variantColor={2}>
                  {moment(model?.created_at)?.fromNow()}
                </Typography>
              </Tooltip>

              <Tag variant={"filled"} size="xsmall" color={MODEL_STATE_TAG_MAPPER[model.state].color}>
                {getLocalizedModelStateName(model.state, t)}
              </Tag>
            </div>
          </div>
        </div>
      </Link>
    </li>
  )
}
